import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Star, MessageSquare, Building2, UserCheck, CalendarCheck, Phone,
  RefreshCw, Search, MessageCircle, Award,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRow = ({ value }) => (
  <div className="flex items-center gap-0.5" data-testid="star-row">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-3.5 h-3.5 ${n <= (value || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
      />
    ))}
    <span className="ml-1 text-xs font-semibold text-slate-600">{value || 0}</span>
  </div>
);

const KpiCard = ({ icon: Icon, label, value, accent }) => (
  <div className={`bg-white rounded-xl border-l-4 ${accent} shadow-sm p-4 flex items-center gap-3`} data-testid={`feedback-kpi-${label}`}>
    <div className="bg-slate-100 p-2.5 rounded-lg">
      <Icon className="w-5 h-5 text-slate-700" />
    </div>
    <div>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold text-slate-900">{value}</span>
        <span className="text-xs text-slate-400">/5</span>
      </div>
    </div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const formatMatchDate = (d, st, et) => {
  if (!d) return '-';
  let out = d;
  try {
    out = new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { out = d; }
  if (st) out += ` ${String(st).slice(0, 5)}${et ? '-' + String(et).slice(0, 5) : ''}`;
  return out;
};

const FeedbackTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ count: 0, averages: {}, items: [] });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchFeedback = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/feedback`);
      setData(res.data || { count: 0, averages: {}, items: [] });
    } catch (e) {
      setError('Geri bildirimler yüklenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFeedback(); }, []);

  const averages = data.averages || {};

  const filteredItems = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    if (!q) return data.items || [];
    return (data.items || []).filter((i) =>
      (i.customer_name || '').toLocaleLowerCase('tr-TR').includes(q) ||
      (i.salon_name || '').toLocaleLowerCase('tr-TR').includes(q) ||
      (i.comment || '').toLocaleLowerCase('tr-TR').includes(q)
    );
  }, [data.items, search]);

  return (
    <div className="space-y-4" data-testid="feedback-tab">
      {/* Hero */}
      <div className="bg-gradient-to-r from-amber-600 via-orange-600 to-rose-600 rounded-xl p-4 text-white shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 p-3 rounded-lg backdrop-blur">
              <MessageSquare className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Müşteri Geri Bildirimleri</h2>
              <p className="text-white/80 text-sm">Salon, hakem, organizasyon ve iletişim puanları</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white/10 px-4 py-2 rounded-lg backdrop-blur text-center">
              <p className="text-xs text-white/70">Toplam</p>
              <p className="text-xl font-bold">{data.count || 0}</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={fetchFeedback}
              data-testid="feedback-refresh-btn"
              className="bg-white/15 hover:bg-white/25 text-white border-0"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
            </Button>
          </div>
        </div>
      </div>

      {/* KPI averages */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard icon={Award} label="Genel" value={averages.genel ?? 0} accent="border-rose-500" />
        <KpiCard icon={Building2} label="Salon" value={averages.salon ?? 0} accent="border-blue-500" />
        <KpiCard icon={UserCheck} label="Hakem" value={averages.hakem ?? 0} accent="border-amber-500" />
        <KpiCard icon={CalendarCheck} label="Organizasyon" value={averages.organizasyon ?? 0} accent="border-emerald-500" />
        <KpiCard icon={Phone} label="İletişim" value={averages.iletisim ?? 0} accent="border-purple-500" />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          placeholder="Müşteri, salon veya yorumda ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="feedback-search-input"
        />
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500" data-testid="feedback-loading">Yükleniyor...</div>
        ) : error ? (
          <div className="p-10 text-center" data-testid="feedback-error">
            <p className="text-rose-600 mb-3">{error}</p>
            <Button onClick={fetchFeedback} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Tekrar Dene
            </Button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="feedback-empty">
            <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            {data.count === 0 ? 'Henüz geri bildirim yok.' : 'Aramanızla eşleşen geri bildirim bulunamadı.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="feedback-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Müşteri</th>
                  <th className="text-left px-4 py-3 font-semibold">Maç Tarihi</th>
                  <th className="text-left px-4 py-3 font-semibold">Salon</th>
                  <th className="text-left px-4 py-3 font-semibold">Salon Puanı</th>
                  <th className="text-left px-4 py-3 font-semibold">Hakem</th>
                  <th className="text-left px-4 py-3 font-semibold">Organizasyon</th>
                  <th className="text-left px-4 py-3 font-semibold">İletişim</th>
                  <th className="text-left px-4 py-3 font-semibold">Yorum</th>
                  <th className="text-left px-4 py-3 font-semibold">Tarih</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredItems.map((i) => (
                  <tr key={i.id} className="hover:bg-slate-50 transition-colors cursor-pointer" data-testid={`feedback-row-${i.id}`} onClick={() => setSelected(i)}>
                    <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{i.customer_name}</td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap" data-testid={`feedback-matchdate-${i.id}`}>
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarCheck className="w-3.5 h-3.5 text-slate-400" />
                        {formatMatchDate(i.match_date, i.match_start_time, i.match_end_time)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{i.salon_name || '-'}</td>
                    <td className="px-4 py-3"><StarRow value={i.salon} /></td>
                    <td className="px-4 py-3"><StarRow value={i.hakem} /></td>
                    <td className="px-4 py-3"><StarRow value={i.organizasyon} /></td>
                    <td className="px-4 py-3"><StarRow value={i.iletisim} /></td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs">
                      {i.comment ? (
                        <span className="flex items-start gap-1.5 text-blue-600 hover:underline" title="Yorumu tam görmek için tıklayın">
                          <MessageCircle className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{i.comment}</span>
                        </span>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(i.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detay penceresi — tam yorum ve puanlar */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="feedback-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-600" /> Geri Bildirim Detayı
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Müşteri</p>
                  <p className="font-semibold text-slate-800">{selected.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Maç Tarihi</p>
                  <p className="font-semibold text-slate-800">{formatMatchDate(selected.match_date, selected.match_start_time, selected.match_end_time)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Salon</p>
                  <p className="font-semibold text-slate-800">{selected.salon_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Gönderim</p>
                  <p className="font-semibold text-slate-800">{formatDate(selected.created_at)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[['Salon', selected.salon], ['Hakem', selected.hakem], ['Organizasyon', selected.organizasyon], ['İletişim', selected.iletisim]].map(([lbl, val]) => (
                  <div key={lbl} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-slate-600">{lbl}</span>
                    <StarRow value={val} />
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Yorum</p>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap break-words" data-testid="feedback-detail-comment">
                  {selected.comment ? selected.comment : <span className="text-slate-400">Yorum bırakılmamış.</span>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackTab;
